<mat-toolbar color="primary" class="action-toolbar">
    <button mat-icon-button (click)="returnToParent()" matTooltip="Back"><mat-icon
            color="primary">arrow_back_ios</mat-icon></button>
    <h3>{{ currentPatient | patientHeading:'Schedule Patient' }}</h3>
    <div>
        <button mat-icon-button *ngIf="editingAppointment && editingAppointment.id" [disabled]="isSaving"
            (click)="deleteAppointment()" matTooltip="Delete Appointemnt"><mat-icon color="warn">delete</mat-icon></button>

        <button mat-icon-button [disabled]="form.invalid || isSaving" (click)="saveAppointment()"
            [matTooltip]="isSaving ? 'Saving…' : 'Save Appointment'">
            <mat-spinner *ngIf="isSaving" diameter="20"></mat-spinner>
            <mat-icon *ngIf="!isSaving" [color]="form.invalid ? '' : 'primary'">save</mat-icon>
        </button>
    </div>
</mat-toolbar>

<div class="appointment-details-container">
    <form [formGroup]="form">
        <mat-card>

            <!--
                25.5 - THE PATIENT CANNOT BE CONTACTED AT ALL.

                Standing warning, at the top of the form, wherever the practice is in the booking.
                The dialog at save time is the one that cannot be missed; this is the one that
                stops the receptionist getting that far before they know. It says nothing when
                notifications are already off, because with sending suppressed the contact details
                change nothing about this booking (25.1).
            -->
            <div class="appointment-unreachable" *ngIf="shouldWarnUnreachable"
                data-test="appointment-unreachable">
                <mat-icon>person_off</mat-icon>
                <div>
                    <strong>This patient cannot be contacted.</strong>
                    No email address and no mobile number, so no confirmation, no reminder and no
                    documents can reach them. Add contact details to the patient to fix it.
                </div>
            </div>

            <!--
                25 / 23.1 - DOCUMENTS THIS APPOINTMENT DID NOT SEND, each with its reason AND its
                remedy, because the three reasons need three different actions. Send-now is offered
                where one makes sense; an unreachable patient gets the remedy instead of a button
                that would mark the row sent and deliver nowhere.
            -->
            <div class="appointment-skipped-documents" *ngIf="skippedDocuments.length && !stateMode"
                data-test="appointment-skipped-documents">
                <h4>
                    <mat-icon>report_problem</mat-icon>
                    {{ skippedDocuments.length === 1 ? 'A document was' : skippedDocuments.length + ' documents were' }}
                    not sent for this appointment
                </h4>
                <div class="skipped-document" *ngFor="let entry of skippedDocuments"
                    [ngClass]="'skip-' + (entry.skip.reason || 'unknown')"
                    data-test="appointment-skipped-document">
                    <div class="skipped-document-text">
                        <div class="skipped-document-name">
                            <strong>{{ documentNameFor(entry) }}</strong>
                            <span class="skipped-document-label">{{ entry.skip.label }}</span>
                        </div>
                        <p class="skipped-document-why" data-test="appointment-skip-why">{{ entry.skip.why }}</p>
                        <p class="skipped-document-remedy" data-test="appointment-skip-remedy">{{ entry.skip.remedy }}</p>
                    </div>
                    <button mat-stroked-button type="button" color="primary"
                        *ngIf="entry.skip.canSendNow"
                        [disabled]="sendingDocumentId === entry.row.id"
                        data-test="appointment-send-now"
                        (click)="sendSkippedDocument(entry)">
                        Send now
                    </button>
                </div>
            </div>

            <mat-form-field appearance="outline" *ngIf="locations.length">
                <mat-label>Location</mat-label>
                <mat-select formControlName="locationId" (selectionChange)="selectLocation($event.value)">
                    <mat-option *ngFor="let location of locations" [value]="location.id">{{location.name}}</mat-option>
                </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Appointment Type</mat-label>
                <mat-select formControlName="appointmentTypeId" (selectionChange)="selectAppointmentType($event)">
                    <mat-option *ngFor="let appointmentType of typeOptions"
                        [value]="appointmentType.id">{{appointmentType.type}}</mat-option>
                </mat-select>
            </mat-form-field>

            <!-- Shown too for a booking started from a doctor's column in the calendar's day view. -->
            <mat-form-field appearance="outline" *ngIf="calendarSplitByPhysician || calendarPhysicianId">
                <mat-label>Treating Physician</mat-label>
                <mat-select formControlName="physicianId">
                    <mat-option [value]="''">—</mat-option>
                    <mat-option *ngFor="let user of groupUsers" [value]="user.userId">{{user.title}} {{user.name}}
                        {{user.surname}}</mat-option>
                </mat-select>
            </mat-form-field>

            <!-- Status Control - Only show for existing appointments (with ID) -->
            <mat-form-field appearance="outline" *ngIf="editingAppointment?.id">
                <mat-label>Appointment Status</mat-label>
                <mat-select formControlName="status" (selectionChange)="updateAppointmentStatus($event.value)">
                    <mat-option *ngFor="let option of statusOptions" 
                        [value]="option.value" 
                        [disabled]="!option.available">
                        {{option.label}}
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <!--
              §30 — THE DOCUMENTS THIS BOOKING WILL SEND.

              Pre-ticked from the appointment TYPE'S triggers, and each chip says WHEN it would go
              ("3 days before the appointment"), because that is what a receptionist needs in front
              of them before overriding one. Configuration pre-ticks; it never forces (§14.3).

              NEW BOOKINGS ONLY: `updateAppointment` raises no documents, so offering the control
              on an edit would let a user tick something that then went nowhere. It also needs the
              type chosen first — that is what it pre-ticks FROM — and it hides itself entirely
              when the practice has no documents.
            -->
            <app-document-attachment-picker
                *ngIf="!editingAppointment && !stateMode && form.get('appointmentTypeId')?.value"
                context="appointment"
                [patientId]="patientId"
                [groupId]="groupId"
                [appointmentTypeId]="form.get('appointmentTypeId')?.value"
                [selection]="selectedDocumentIds"
                label="Documents to send for this appointment"
                (selectionChange)="onDocumentSelectionChange($event)"
                data-test="appointment-document-picker">
            </app-document-attachment-picker>

            <!--
                Notify Patient Toggle.

                §25.1 — DISABLED, AND FORCED ON, while documents are attached. A document reaches a
                patient as a link in a message, and `notifyPatient === false` gates the entire
                document block server-side: the practice would tick documents, save, and find every
                one of them raised as `skipped`/`suppressed` on the patient file afterwards.
                Unticking the documents releases the toggle.
            -->
            <mat-slide-toggle class="m-b-15 m-t-10" [(ngModel)]="notifyPatient" [ngModelOptions]="{ standalone: true }"
                [disabled]="mustNotifyForDocuments" data-test="notify-patient"
                *ngIf="!suppressSending && form.get('appointmentTypeId')?.value">
                {{notifyPatientLabel}}
            </mat-slide-toggle>
            <!-- Follows the appointment type; changing it here applies to this booking only. -->
            <mat-slide-toggle class="m-b-15" [ngModel]="sendReminder" (ngModelChange)="setSendReminder($event)"
                [ngModelOptions]="{ standalone: true }" data-test="send-reminder"
                *ngIf="!suppressSending && form.get('appointmentTypeId')?.value">
                Send the patient a reminder
            </mat-slide-toggle>
            <p class="hint m-b-15" *ngIf="mustNotifyForDocuments" data-test="notify-forced">
                The patient has to be notified — the documents above are sent as a link in that
                message. Remove them to turn this off.
            </p>

            <mat-form-field appearance="outline">
                <mat-label>Date</mat-label>
                <input matInput [matDatepicker]="appointmentDatePicker" formControlName="date"
                    [matDatepickerFilter]="isDateAvailable" placeholder="DD/MM/YYYY">
                <mat-datepicker-toggle matSuffix [for]="appointmentDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #appointmentDatePicker></mat-datepicker>
            </mat-form-field>

            <!-- <mat-form-field appearance="outline">
                <mat-label>Time Slot</mat-label>
                <mat-select formControlName="timeSlot">
                    <mat-option *ngFor="let slot of timeSlots" [value]="slot.value">
                        {{ slot.viewValue }}
                    </mat-option>
                </mat-select>
            </mat-form-field> -->

            <mat-form-field appearance="outline">
                <mat-label>Start Time</mat-label>
                <input matInput formControlName="timeSlot" [ngxTimepicker]="startTimePicker" [format]="24">
                <ngx-material-timepicker #startTimePicker [theme]="darkTheme" [appendToInput]="true"
                    (timeSet)="onStartTimeSet($event)" [preventOverlayClick]="true"></ngx-material-timepicker>
            </mat-form-field>


            <mat-form-field appearance="outline">
                <mat-label>End Time</mat-label>
                <input matInput formControlName="endSlot" [ngxTimepicker]="endTimePicker" [format]="24">
                <ngx-material-timepicker #endTimePicker [theme]="darkTheme" [appendToInput]="true"
                    (timeSet)="onEndTimeSet()" [preventOverlayClick]="true"></ngx-material-timepicker>
                <mat-error *ngIf="form.get('endSlot')?.hasError('invalidEndSlot')">
                    End time must be after start time
                </mat-error>
            </mat-form-field>

            <ng-container *ngIf="!isProcedure">
                <mat-form-field appearance="outline">
                    <mat-label>Reason For Visit</mat-label>
                    <input matInput formControlName="reasonForVisit">
                </mat-form-field>
            </ng-container>

            <ng-container *ngIf="isProcedure">
                <!-- Where the procedure happens. Prints on the theatre booking form's header
                     line, so it is per-appointment and not per-appointment-type. -->
                <mat-form-field appearance="outline">
                    <mat-label>Hospital</mat-label>
                    <mat-select formControlName="hospitalId">
                        <mat-option [value]="null">—</mat-option>
                        <mat-option *ngFor="let hospital of hospitals" [value]="hospital.id">
                            {{hospital.name}}
                        </mat-option>
                    </mat-select>
                </mat-form-field>

                <mat-form-field appearance="outline">
                    <mat-label>Procedure</mat-label>
                    <input matInput formControlName="procedure">
                </mat-form-field>

                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Starve Time</mat-label>
                    <input matInput formControlName="starveTime" [ngxTimepicker]="starveTimePicker" [format]="24">
                    <ngx-material-timepicker #starveTimePicker [theme]="darkTheme" [appendToInput]="true"
                        [preventOverlayClick]="true"></ngx-material-timepicker>
                </mat-form-field>

                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Hospital Arrive Time</mat-label>
                    <input matInput formControlName="arriveTime" [ngxTimepicker]="arriveTimePicker" [format]="24">
                    <ngx-material-timepicker #arriveTimePicker [theme]="darkTheme" [appendToInput]="true"
                        [preventOverlayClick]="true"></ngx-material-timepicker>
                </mat-form-field>
            </ng-container>

            <!-- ═══ §3b — the theatre list is a day export, so its inputs live here ═══════════
                 Anaesthetist, the two assistants, anaesthesia type, theatre number, post-op ward
                 and special instructions used to be typed on /practice/theatre-lists. That page
                 is gone; these are the same fields, on the appointment itself.

                 NOTHING HERE IS PRE-FILLED from another booking (§3b decision 3).

                 Its own `*ngIf` rather than sharing the block above, so this gate can be broken
                 on its own in a mutation run — see `theatre-appointment-fields.spec.ts`. -->
            <ng-container *ngIf="isProcedure">
                <mat-form-field appearance="outline" class="theatre-field">
                    <mat-label>Anaesthetist</mat-label>
                    <mat-select formControlName="anaesthetistColleagueId">
                        <mat-option [value]="null">—</mat-option>
                        <mat-option *ngFor="let colleague of anaesthetists" [value]="colleague.id"
                            class="anaesthetist-option">
                            {{ colleagueLabel(colleague) }}
                        </mat-option>
                    </mat-select>
                </mat-form-field>

                <mat-form-field appearance="outline" class="theatre-field">
                    <mat-label>Surgical Assistant 1</mat-label>
                    <mat-select formControlName="assistant1ColleagueId">
                        <mat-option [value]="null">—</mat-option>
                        <mat-option *ngFor="let colleague of surgicalAssistants" [value]="colleague.id"
                            class="assistant1-option">
                            {{ colleagueLabel(colleague) }}
                        </mat-option>
                    </mat-select>
                </mat-form-field>

                <mat-form-field appearance="outline" class="theatre-field">
                    <mat-label>Surgical Assistant 2</mat-label>
                    <mat-select formControlName="assistant2ColleagueId">
                        <mat-option [value]="null">—</mat-option>
                        <mat-option *ngFor="let colleague of surgicalAssistants" [value]="colleague.id"
                            class="assistant2-option">
                            {{ colleagueLabel(colleague) }}
                        </mat-option>
                    </mat-select>
                </mat-form-field>

                <!-- §5.5: the anaesthetic, not the anaesthetist. Full ANESTHESIA strings —
                     a short form is not one of the option values and would be dropped. -->
                <mat-form-field appearance="outline" class="theatre-field">
                    <mat-label>Anaesthesia Type</mat-label>
                    <mat-select formControlName="anaesthesiaType">
                        <mat-option [value]="null">—</mat-option>
                        <mat-option *ngFor="let anaesthesia of anaesthesiaTypes" [value]="anaesthesia.value"
                            class="anaesthesia-option">
                            {{ anaesthesia.text }}
                        </mat-option>
                    </mat-select>
                </mat-form-field>

                <mat-form-field appearance="outline" class="theatre-field">
                    <mat-label>Theatre Number</mat-label>
                    <input matInput formControlName="theatreNumber">
                </mat-form-field>

                <mat-form-field appearance="outline" class="theatre-field">
                    <mat-label>Post-op Ward</mat-label>
                    <input matInput formControlName="postOpWard">
                </mat-form-field>

                <mat-form-field appearance="outline" class="full-width theatre-field">
                    <mat-label>Special Instructions</mat-label>
                    <textarea matInput formControlName="specialInstructions" rows="3"
                        placeholder="Implants, latex allergy, positioning, blood products…"></textarea>
                </mat-form-field>

                <!-- ═══ §3d — THE CODES THIS BOOKING PLANS ═══════════════════════════════════
                     Shown on EVERY theatre booking, in all three workflows. Prefilled from the
                     letter of motivation when there is exactly one unbooked letter, typed by
                     hand when there is none, and editable either way — the booking is the most
                     recent statement of intent, and the note-save comparison measures against
                     what is here rather than against the letter.

                     These are NOT form controls. They are a replace-set written by
                     `savePlannedCodes()` once the appointment has an id, because
                     `PlannedProcedureCodes` is its own table keyed on `appointmentId` and a new
                     booking has no id until the POST comes back. -->
                <div class="full-width planned-codes-block" *ngIf="showPlannedCodes">
                    <h4 class="planned-codes-heading">Procedure Codes for this Booking</h4>

                    <!-- Where they came from. A prefilled list that does not say it was
                         prefilled is a list the user has no reason to check. -->
                    <p class="planned-codes-source" *ngIf="motivationBaseline">
                        <mat-icon inline="true">description</mat-icon>
                        Prefilled from the letter of motivation of {{ motivationBaselineDate }}.
                    </p>

                    <app-procedure-code-selector [initialCodes]="plannedCodes"
                        (codesChanged)="onPlannedCodesChanged($event)" [patient]="currentPatient"
                        [hintText]="'These codes print on the theatre booking form and are compared against the procedure note.'">
                    </app-procedure-code-selector>

                    <!-- The drift marker. PASSIVE ON PURPOSE — see `motivationDrift` in the
                         component for why this is not a dialog. The user is editing a list we
                         prefilled seconds ago; warning them about their own edit is how a
                         warning gets ignored. Changing the codes here is allowed and expected,
                         and this only makes it visible. -->
                    <div class="planned-codes-drift" *ngIf="motivationDrift as drift">
                        <mat-icon>edit_note</mat-icon>
                        <div class="drift-body">
                            <p><strong>Changed from the letter of motivation.</strong>
                                These codes will be used, not the letter's.</p>
                            <p *ngIf="drift.added.length">
                                <strong>Added:</strong> {{ drift.added.join(', ') }}
                            </p>
                            <p *ngIf="drift.removed.length">
                                <strong>Removed:</strong> {{ drift.removed.join(', ') }}
                            </p>
                        </div>
                    </div>
                </div>
            </ng-container>

            <mat-form-field appearance="outline" *ngIf="!isProcedure">
                <mat-label>Virtual Meeting Link</mat-label>
                <input matInput formControlName="virtualLink">
            </mat-form-field>

            <!-- APPOINTMENT MESSAGES Section -->
            <mat-expansion-panel
                *ngIf="!suppressSending && form.get('appointmentType')?.value && form.get('date')?.value && form.get('timeSlot')?.value"
                class="flat fill">
                <mat-expansion-panel-header>
                    <mat-panel-title>
                        APPOINTMENT MESSAGES
                    </mat-panel-title>
                </mat-expansion-panel-header>
                <mat-tab-group>
                    <mat-tab label="Confirmation">
                        <mat-form-field appearance="outline" class="full-width m-t-10">
                            <mat-label>
                                {{
                                messageTemplates.isProcedure ?
                                'Theatre Confirmation Message' :
                                'Appointment Confirmation Message'
                                }}
                            </mat-label>
                            <textarea matInput [(ngModel)]="messageTemplates.confirmation"
                                [ngModelOptions]="{ standalone: true }" rows="6"
                                placeholder="Enter confirmation email text here">
                            </textarea>
                        </mat-form-field>

                    </mat-tab>
                    <mat-tab label="Reschedule">
                        <mat-form-field appearance="outline" class="full-width m-t-10">
                            <mat-label>
                                {{
                                messageTemplates.isProcedure
                                ? 'Theatre Rescheduling Message'
                                : 'Appointment Rescheduling Message'
                                }}
                            </mat-label>
                            <textarea matInput [(ngModel)]="messageTemplates.reschedule"
                                [ngModelOptions]="{ standalone: true }" rows="6"
                                placeholder="Enter confirmation email text here">
                            </textarea>
                        </mat-form-field>

                    </mat-tab>
                    <mat-tab label="Cancellation">
                        <mat-form-field appearance="outline" class="full-width m-t-10">
                            <mat-label>
                                {{
                                messageTemplates.isProcedure ?
                                'Theatre Cancellation Message' :
                                'Appointment Cancellation Message'
                                }}
                            </mat-label>
                            <textarea matInput [(ngModel)]="messageTemplates.cancellation"
                                [ngModelOptions]="{ standalone: true }" rows="6"
                                placeholder="Enter cancellation email text here">
                            </textarea>
                        </mat-form-field>

                    </mat-tab>
                    <mat-tab label="Reminder">
                        <mat-form-field appearance="outline" class="full-width m-t-10">
                            <mat-label>
                                {{
                                messageTemplates.isProcedure ?
                                'Theatre Reminder Message' :
                                'Appointment Reminder Message'
                                }}
                            </mat-label>
                            <textarea matInput [(ngModel)]="messageTemplates.reminder"
                                [ngModelOptions]="{ standalone: true }" rows="6"
                                placeholder="Enter confirmation email text here"></textarea>
                        </mat-form-field>
                    </mat-tab>
                </mat-tab-group>
            </mat-expansion-panel>

            <p class="hint" *ngIf="sendReminder && !suppressSending">Note: An appointment reminder will be sent to this patient at 6 AM on the day of the
                appointment if they have an email address on their file.</p>

        </mat-card>
    </form>
</div>